import { BidStructure, SamplePlanCountry } from '../sections/bids/bid-spec-parsing';
import { TemplateRow } from 'core';

// Matches backend NavigatorAPI.Enums.BidStatus
export enum BidStatus {
  InProgress   = 0,
  SentToClient = 1,
  Sold         = 2,
  Lost         = 3
}

export const BID_STATUSES: Record<BidStatus, string> = {
  [BidStatus.InProgress]:   'In Progress',
  [BidStatus.SentToClient]: 'Sent to Client',
  [BidStatus.Sold]:         'Closed Won',
  [BidStatus.Lost]:         'Closed Lost',
};

export type TrackerFrequency = 'Monthly' | 'Quarterly' | 'Bi-Annual' | 'Yearly';

export const TRACKER_FREQUENCIES: TrackerFrequency[] = ['Monthly', 'Quarterly', 'Bi-Annual', 'Yearly'];

/** How a tracker bid continues its tracker: the next wave of the tracker year, or a renewal (new year). */
export type TrackerCloneType = 'wave' | 'renewal';

/** What a full-year tracker's vendors are asked to quote: one wave (the default) or the whole year. */
export type VendorQuoteBasis = 'wave' | 'year';

export interface BidFullYearPricingLine {
  name: string;
  /** e.g. "1,200 completes at $4.50 CPI" for a sample line; blank for a service. */
  detail: string;
  internalCost: number;
  clientPrice: number;
}

/** One pricing option as the client saw it, services included. */
export interface BidFullYearPricingOption {
  label: string;
  lines: BidFullYearPricingLine[];
  internalCost: number;
  clientPrice: number;
}

/** The full year as confirmed at Closed Won, in the bid's own shape, so a renewal can reopen it. The
 *  bid itself is saved as wave 1 at that point, so this is the only place the full-year figures stay.
 *  Holds only the option(s) the client bought, keyed to this smaller plan. */
export interface BidFullYearSource {
  samplePlan: SamplePlanCountry[];
  commitments: BidCommitmentRow[];
  vendorScenarios: BidVendorScenarioRow[];
  serviceSelections: BidServiceSelection[];
  serviceBids: BidServiceBid[];
}

export interface BidFullYearPricing {
  /** ISO timestamp of the send this copy was taken at; null when the bid was won without being sent. */
  sentAt: string | null;
  /** ISO timestamp of Closed Won, when the copy was locked and the bid itself became wave 1. */
  lockedAt: string | null;
  frequency: TrackerFrequency;
  wavesPerYear: number;
  options: BidFullYearPricingOption[];
  /** Set at Closed Won only; the Sent to Client copy is for display. */
  source?: BidFullYearSource | null;
}

export const BID_STATUS_OPTIONS: { value: BidStatus; label: string }[] = [
  { value: BidStatus.SentToClient, label: 'Sent to Client' },
  { value: BidStatus.Sold,         label: 'Closed Won' },
  { value: BidStatus.Lost,         label: 'Closed Lost' },
];

// Selectable reasons captured in the Closed Lost modal when a bid is marked Lost.
export const BID_LOST_REASONS: string[] = [
  'Competition',
  'Expectation Mismatch',
  'Future Interest',
  'Lack of response',
  'Missed Follow Ups',
  'Other',
  'Price',
  'Unqualified Customer',
  'Wrong Target',
];

// One configured service in the Services step (step 2). Mirrors backend BidServiceSelectionDto.
// Replaces the former servicesStateJson blob; ids (serviceId, selectedTier, addon ids) are
// Guid strings on the wire.
export interface BidServiceSelection {
  id?: string;
  serviceId: string;
  enabled: boolean;
  hours: number | null;
  fixedPrice: number | null;
  amount: number | null;
  selectedTier: string | null;
  selectedAddons: string[];
  // Rate snapshot captured on first save so later default changes only affect new bids; null on
  // legacy bids (the client falls back to the live template/rate-card rate until the next save).
  markup?: number | null;       // client-price markup % (Labor / Fixed / Custom)
  hourlyRate?: number | null;   // Labor unit rate
  fixedRate?: number | null;    // Fixed template rate (when fixedPrice not overridden)
  tierRate?: number | null;     // Rate Card selected-tier rate
  addonsTotal?: number | null;  // Rate Card sum of selected addon prices
}

// One vendor quoted on a service in the Services Bids step (step 6). Mirrors backend
// BidServiceBidVendorDto; ids are Guid strings on the wire.
export interface BidServiceBidVendor {
  id?: string;
  vendorId: string;
  quote: number | null;
  selected: boolean;
}

// One vendor-priced service with its vendor quotes. Mirrors backend BidServiceBidDto.
// Replaces the former servicesBidsStateJson blob.
export interface BidServiceBid {
  id?: string;
  serviceId: string;
  markup: number | null;
  vendors: BidServiceBidVendor[];
}

// ── Vendor Bids step (step 5) — normalized from the former vendorBidsStateJson blob ──
// Mirror the backend BidVendorDto / BidVendorScenarioDto / BidVendorScenarioBidDto.

// One vendor added to the bid. `countries` is Global-mode coverage only.
export interface BidVendorRow {
  id?: string;
  vendorId: string;
  sent: boolean;
  sentAt: string | null;
  respondedAt?: string | null;   // server-stamped when the vendor submits; read-only on the client
  respondedByName?: string | null;   // name of the vendor user who submitted; read-only on the client
  respondedByUserId?: string | null;   // id of the vendor user who submitted; read-only on the client
  note?: string;
  countries?: string[];
}

// Per-vendor outcome of sending a bid request email; the client only marks a vendor sent on success.
export interface VendorSendResult {
  vendorId: string;
  success: boolean;
  error?: string;
}

// One vendor's bid on a scenario. feas/cpi/dif are raw string inputs.
export interface BidVendorScenarioBidRow {
  id?: string;
  vendorId: string;
  feas?: string;
  cpi?: string;
  dif?: string;
  selected?: boolean;
  feasSource?: 'manual' | 'portal';
  cpiSource?: 'manual' | 'portal';
  difSource?: 'manual' | 'portal';
  allocN?: number;
}

// One scenario row. `key` is the stable "countryCode:specIndex" logical key.
export interface BidVendorScenarioRow {
  id?: string;
  key: string;
  sellCpi?: number;
  optionName?: string;
  bids: BidVendorScenarioBidRow[];
}

// One committed sample size on a bid: what we bid, as opposed to the client's ask (the requested N
// on the sample plan) or what they bought (the sold N on the closed-won snapshot). Keyed by the
// stable "countryCode:specIndex" scenario key the vendor-bids grid and the closed-won snapshot
// already share, so it pairs to a spec without a new identifier.
export interface BidCommitmentRow {
  id?: string;
  key: string;
  n: number;
}

export interface Bid {
  id: string;
  number: string;
  name: string;
  clientId: string;
  clientContactId: string;
  accountOwnerId: string;
  salesRepId?: string;
  primarySalesOpsId?: string;
  zohoDealId?: string;
  /**
   * Vendor-bids scenario keys (`${code}:${specIdx}`) of the option(s) chosen when the Zoho deal was
   * created at Sent-to-Client. Server-set on the deal call — values sent on a normal bid save are ignored.
   */
  sentOptionKeys?: string[];
  clientBudget: number;
  startDate: string;
  endDate: string;
  /** Private note edited on the Summary step; a separate field from specText (Proposal step). */
  internalNotes?: string;
  projectTypeId: string;
  status: BidStatus;
  /** Vendor-facing status ("Open" / "Submitted"), set by the API for vendor sessions only. */
  vendorStatus?: string;
  /** Vendor-facing: true once this vendor's bid is "Used" in the field plan — their grid is read-only. */
  locked?: boolean;
  clientName?: string;
  clientContactName?: string;
  accountOwnerName?: string;
  salesRepName?: string;
  primarySalesOpsName?: string;
  projectTypeName?: string;
  serviceSelections?: BidServiceSelection[];
  serviceBids?: BidServiceBid[];
  /** Templates removed from the project type that this bid still references (each `deleted: true`). */
  serviceTemplates?: TemplateRow[];
  samplePlan?: SamplePlanCountry[];
  /** Chosen sample plan structure. Null on bids saved before this existed —
   *  readers fall back to deriving it from the plan shape (`deriveStructure`). */
  structure?: BidStructure | null;
  vendors?: BidVendorRow[];
  vendorScenarios?: BidVendorScenarioRow[];
  fpAudExclusions?: string[];
  maBidMode?: string;
  /** Null or absent reads as 'wave'. Only a full-year tracker (`fullYearWaves`) offers 'year'. */
  vendorQuoteBasis?: VendorQuoteBasis | null;
  allVendorsNote?: string;
  /** Committed sample sizes per scenario key: what the bid commits to delivering, typed on the
   *  Summary step. Nothing derives into it, so an absent key means nobody has committed yet and
   *  the line quotes the client's ask (the requested N on the sample plan). */
  commitments?: BidCommitmentRow[];
  /** The pasted RFP/spec text the bid was drafted from (Proposal step "Spec" field).
   *  Written by the parse landing at creation; documentation only after that. */
  specText?: string;
  /** Bid-level LOI (minutes), entered on the Proposal step when the project type has no Sample
   *  service. Feeds Rate Card pricing (rate × LOI) in place of the sample-plan LOI. */
  loi?: number | null;
  /** Bid-level completes, captured on the Proposal step when there's no Sample service. Stored for
   *  record and carried into the created project; not used in any cost formula. */
  completes?: number | null;
  /** Optional tracker tag (Proposal step, shown when Tracker is selected); copied onto each project
   *  created from the bid. */
  trackerTag?: string | null;
  /** Proposal step tracker flag. The API derives it from `trackerFrequency` on save; the save also
   *  copies the choice onto each sample plan audience, where the Gut Check reads it. */
  isTracker?: boolean;
  trackerFrequency?: TrackerFrequency | null;
  /** yyyy-MM-dd. */
  firstWaveFieldStart?: string | null;
  /** Set when a tracker bid is cloned: the next wave of this tracker year, or a renewal (new tracker
   *  year). The API keeps it from the bid's first save only. */
  trackerCloneType?: TrackerCloneType | null;
  /** The bid a wave or renewal was cloned from; stored with `trackerCloneType` on the first save. */
  clonedFromBidId?: string | null;
  /** The tracker record this bid belongs to. On create it links a bid that wasn't cloned to an existing
   *  tracker (with `trackerCloneType`); later changes go through `BidService.linkTracker`. */
  trackerRecordId?: string | null;
  /** CCC-YY-N00 (e.g. ORH-26-100), generated when a tracker bid is Closed Won; the year moves on at
   *  renewal and the tracker number stays.
   *  Read-only: the API ignores it on a save. */
  trackerId?: string | null;
  /** Copy of a tracker bid's pricing as last Sent to Client, locked at Closed Won. Sent with the save;
   *  not stored by the API yet. */
  fullYearPricing?: BidFullYearPricing | null;
  /** Signature of the sample plan the Gut Check was last run against; drives Gut Check completion. */
  gutCheckSignature?: string | null;
  sentToClientAt?: string;
  wonAt?: string;
  lostAt?: string;
  /** Reason recorded in the Closed Lost modal when the bid was marked Lost. */
  lostReason?: string | null;
  convertedAt?: string;
  /** CSM handling the project(s) created from this bid — i.e. the created project's PM.
   *  Server-derived for converted (Sold) bids only; `null`/absent means no PM has been assigned
   *  yet, which is the "awaiting CSM handoff" state the bid dashboard filters on.
   *  Backend spec: `.claude/features/NK-222/csm-handoff-assignment.md`. */
  csmUserId?: string | null;
  csmName?: string | null;
  /** The Closed Won snapshot — what was actually sold (drives the read-only Sold view).
   *  Written only by create-projects at conversion; values sent on a bid save are ignored.
   *  Null/absent until the bid is converted (or when it was auto-converted without the wizard). */
  wonDetails?: BidWonDetails | null;
  /** Server-stamped last-modified time; used to detect concurrent edits. */
  lastUpdated?: string;
}

/** Lightweight freshness probe returned by GET /bid/{id}/meta. */
export interface BidMeta {
  id: string;
  lastUpdated: string;
}

// Result of POST /bid/{id}/create-projects — matches NavigatorAPI CreateProjectsFromBidResultDto.
export interface CreatedProjectSummary {
  id: string;
  projectCode: string;
  projectName: string;
  warnings: string[];
}

export interface FailedProjectSummary {
  label: string;
  error: string;
}

export interface CreateProjectsFromBidResult {
  bidNumber: string;
  /** True when the projects already existed (bid was already converted) — nothing new was created. */
  alreadyExists: boolean;
  created: CreatedProjectSummary[];
  failed: FailedProjectSummary[];
  /** For the CSM when a wave was cloned from the last one: projects not carried over, or why nothing was cloned. */
  notes?: string[];
}

/**
 * Per-project details confirmed in the Closed Won wizard — one per country for a Global bid, a single
 * entry otherwise. Sent with POST /bid/{id}/create-projects so each created project gets its own name
 * and date windows. Dates are ISO strings (UTC-noon) the API binds to DateTime?; null leaves the
 * backend default (bid-level dates). Matches NavigatorAPI CreateProjectsFromBidProjectDetailDto.
 */
export interface CreateProjectsFromBidProjectDetail {
  /** Country ISO2 for Global (matches the backend group); ignored for a single-project bid. */
  code: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
  fieldStartDate: string | null;
  fieldEndDate: string | null;
}

// ── The Closed Won snapshot (Bid.WonDetails) — matches NavigatorAPI BidWonDetailsDto ──────────────

/** One vendor's share of a sold spec, as confirmed in the Closed Won wizard. */
export interface BidWonVendorLine {
  vendorId: string;
  vendorName: string;
  allocN: number | null;
  cpi: number | null;
}

/** A sold spec as confirmed/edited in the Closed Won wizard (may differ from the proposal). */
export interface BidWonSpec {
  /** Scenario key `${code}:${specIdx}`. */
  key: string;
  segment: string;
  n: number | null;
  /** Whole percent (20 = 20%). */
  ir: number | null;
  /** Minutes. */
  loi: number | null;
  clientCpi: number | null;
  vendors: BidWonVendorLine[];
}

/** One option group in the sale (country for Global, audience for Multi-Audience) and which option sold. */
export interface BidWonGroup {
  /** Group code — country ISO2 for Global, `aud{i}` for Multi-Audience, the country code for Standard. */
  code: string;
  /** Group display name (country or audience label); empty for a Standard single group. */
  name: string;
  /** Label of the option that sold for this group (e.g. "Option 2" or a custom name). */
  optionLabel: string;
}

/** The confirmed audience targeting (category/GTA carry into the project). */
export interface BidWonTargeting {
  /** Group code the audience belongs to (country ISO2 / `aud{i}`) — matches project groups at conversion. */
  code: string;
  segment: string;
  category: string | null;
  gta: string | null;
}

/** One financial line of the won deal — a service (flat amount) or a sample segment (qty × rate). */
export interface BidWonItem {
  name: string;
  description: string;
  qty?: number;
  /** Whole percent. */
  ir?: number;
  /** Minutes. */
  loi?: number;
  /** Client CPI. */
  rate?: number;
  /** Flat client price for service lines. */
  amount?: number;
}

/**
 * The Closed Won snapshot persisted onto `Bid.WonDetails` by create-projects — the single source of
 * truth for everything the wizard confirmed. The bid itself stays a pristine record of the proposal:
 * the server builds the created projects from `specs`/`projects`/`targeting`, the Zoho won-deal
 * update reads `amount` + `items`, and the Sold view renders it all read-only.
 */
/** Whether setting Sent to Client from a status dropdown also creates the Zoho deal (as Summary's Mark
 *  as Sent does). */
export type BidSendChoice = 'deal' | 'noDeal';

/** How a renewal's projects are created at Closed Won: from the bid, or by cloning the tracker's last wave. */
export type BidWonProjectSetup = 'scratch' | 'cloneLastWave';

export interface BidWonDetails {
  version: 1;
  /** Renewals only. A wave bid always clones the last wave; a renewal does only with 'cloneLastWave'. */
  projectSetup?: BidWonProjectSetup;
  /** Server-stamped at conversion. */
  wonAt?: string;
  clientContactId: string | null;
  /** Winning scenario keys — the same set passed as create-projects `selectedKeys`. */
  selectedKeys: string[];
  /** One entry per option group with the sold option's label (display data for the Sold view). */
  groups: BidWonGroup[];
  specs: BidWonSpec[];
  /** One entry per country for Global (matched server-side by ISO2 `code`), a single entry otherwise. */
  projects: CreateProjectsFromBidProjectDetail[];
  targeting: BidWonTargeting[];
  /** Confirmed deal total (services once + selected sample) — becomes the Zoho deal Amount. */
  amount: number;
  /** Deal Items subform lines built from the EDITED wizard state. */
  items: BidWonItem[];
}
